import * as React from 'react';
import { connect } from 'react-redux';
import { get, isEqual, isNil } from 'lodash';

import PageSection from 'components/page/page-section/PageSection';
import { Form, FormActions, FormField, FormFooter } from 'components/form';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';
import HelpNote from 'components/help-note/HelpNote';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';
import CampaignMainImageUpload from 'modules/campaigns/components/upload/CampaignMainImageUpload';
import CampaignAssets from 'modules/campaigns/components/CampaignAssets';
import PromptExit from 'libs/router/prompt-exit/PromptExit';
import DatePickerMulti from 'components/date-picker/date-picker-multi/DatePickerMultiLazy';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import CampaignMetaFields from 'components/campaign-meta-fields/CampaignMetaFields';

import { closeNotification } from 'redux-store/reducers/common-reducer';
import { fetchTeamMembers } from 'modules/organizations/reducers/teamMembers';

import {
  LONG_STRING_MAX_LENGTH,
  NAME_MAX_LENGTH,
  isPositiveNumber,
  isRequired,
  isRequiredRichText,
  maxLength
} from 'utils/validators';
import { __t, applyTranslations } from 'utils/language';
import { getHTMLDate } from 'utils/date';
import { CAMPAIGN_CATEGORIES, triggerCampaignTutorial } from 'utils/campaign';
import { getFullName } from 'utils/view';
import { isManagedServiceSubscription } from 'utils/subscription';
import { isAdmin as isAdminAccount } from 'utils/account';

import type {
  Campaign,
  CampaignAsset,
  CampaignForm,
  CampaignOverview,
  MixedSelectDropdownItem,
  OrganizationMeta,
  OrganizationMetaCommonFields,
  OrganizationSettings,
  PromiseAction
} from 'types';

import './campaign-brief-form.scss';

applyTranslations(require('./campaign-brief-form.i18n.json'));

type OwnProps = {
  inline?: boolean,
  title?: string,
  campaign: Campaign | undefined | null,
  campaignAssets: {
    assets: Array<CampaignAsset>,
    mainImage: CampaignAsset | undefined | null,
    isUpdated: boolean
  },
  campaignOverview?: CampaignOverview | null,
  onSubmit: (b: CampaignForm, a: boolean) => PromiseAction | void,
  onCancel: () => PromiseAction | void
};

type StateProps = {
  teamMembers: Array<MixedSelectDropdownItem>,
  currentOrganizationId: number,
  isManagedService: boolean,
  isAdmin: boolean,
  organizationSettings: OrganizationSettings,
  isZalandoMerchantsFieldHidden: boolean
};

type DispatchProps = {
  dispatchCloseNotification: () => Promise<any>,
  dispatchFetchTeamMembers: (a: number) => Promise<any>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  briefDetails: CampaignForm,
  isUpdated: boolean,
  isSubmitting: boolean
};

export class CampaignBriefForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      briefDetails: this.getBriefDetailsState(props),
      isUpdated: false,
      isSubmitting: false
    };
  }

  componentDidMount() {
    const { currentOrganizationId, dispatchFetchTeamMembers } = this.props;

    if (!get(this.props.campaign, 'id', null)) {
      triggerCampaignTutorial();
    }

    if (currentOrganizationId) {
      dispatchFetchTeamMembers(currentOrganizationId);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { campaign, teamMembers } = this.props;

    if (campaign && !isEqual(prevProps.teamMembers, teamMembers)) {
      this.setState(state => ({ // eslint-disable-line react/no-did-update-set-state
        briefDetails: {
          ...state.briefDetails,
          managers: this.getActiveManagers(campaign, teamMembers)
        }
      }));
    }
  }

  doesOfferExist(): boolean {
    const { campaignOverview } = this.props;

    if (isNil(campaignOverview)) {
      return false;
    }

    const creators: Array<number> = [];

    Object.keys(campaignOverview).forEach(key => {
      const item = campaignOverview[key];

      if (key !== 'draftOverview' && !isNil(item) && !isNil(item.creators) && item.creators > 0) {
        creators.push(item.creators);
      }
    });

    return creators.length > 0;
  }

  getBriefDetailsState(nextProps: Props): CampaignForm {
    const { campaign, teamMembers, organizationSettings } = nextProps;
    const emptyBriefDetails: CampaignForm = {
      name: '',
      budget: null,
      description: '',
      startDate: null,
      endDate: null,
      managers: [],
      additionalRequirementsTemplate: '',
      zalandoMeta: {
        brands: organizationSettings?.zalandoMeta?.brands || [],
        merchants: organizationSettings?.zalandoMeta?.merchants || []
      },
      category: undefined
    };
    const briefDetails: CampaignForm = { ...emptyBriefDetails };

    if (!campaign) {
      return emptyBriefDetails;
    }

    Object.keys(emptyBriefDetails).forEach((field: string) => {
      briefDetails[field] = campaign[field] || emptyBriefDetails[field];

      if (briefDetails[field] && (field === 'startDate' || field === 'endDate')) {
        // $FlowIgnore
        briefDetails[field] = getHTMLDate(briefDetails[field]);
      }
    });

    briefDetails.managers = this.getActiveManagers(campaign, teamMembers);

    return briefDetails;
  }

  getActiveManagers = (campaign: Campaign, teamMembers: Array<MixedSelectDropdownItem>): Array<MixedSelectDropdownItem> => {
    const managers = (campaign.managers || []).map(manager => `${manager.userId}`);
    return teamMembers.filter(member => member.value && managers.includes(member.value));
  };

  handleDateChange = (startDate?: string | null, endDate?: string | null): void => {
    this.handleFieldChanged('startDate', startDate);
    this.handleFieldChanged('endDate', endDate);
  };

  handleManagersChange = (items: Array<MixedSelectDropdownItem> = []) => {
    this.handleFieldChanged('managers', items.filter((entry) => entry.isSelected));
  };

  handleMetaFieldChange = (metaFieldType: 'brands' | 'merchants', items: Array<OrganizationMetaCommonFields> = []) => {
    const { zalandoMeta } = this.state.briefDetails;

    this.handleFieldChanged('zalandoMeta', {
      ...zalandoMeta,
      [metaFieldType]: items || []
    });
  };

  handleFieldChanged = (
    field: string,
    value: string | number | Array<MixedSelectDropdownItem> | undefined | null | OrganizationMeta
  ): void => {
    this.setState(state => ({
      briefDetails: {
        ...state.briefDetails,
        [field]: value
      },
      isUpdated: true
    }));
  };

  handleResetToDefault = (metaFieldType: 'brands' | 'merchants') => {
    const { organizationSettings } = this.props;

    this.setState(state => ({
      briefDetails: {
        ...state.briefDetails,
        zalandoMeta: {
          ...state.briefDetails.zalandoMeta,
          [metaFieldType]: organizationSettings?.zalandoMeta?.[metaFieldType] || []
        }
      },
      isUpdated: true
    }));
  };

  handleSubmit = () => {
    const { isZalandoMerchantsFieldHidden, onSubmit, dispatchCloseNotification } = this.props;
    const { briefDetails, isUpdated } = this.state;

    this.setState({ isSubmitting: true });

    dispatchCloseNotification();
    onSubmit(
      briefDetails,
      isUpdated || (isZalandoMerchantsFieldHidden && (briefDetails.zalandoMeta?.merchants || []).length > 0)
    );
  };

  renderName() {
    const { briefDetails } = this.state;
    return (
      <div className="campaign-brief-form__field-name">
        <FormField
          showRequiredMark
          showValidationIcon={false}
          fontSize="small"
          label={__t('campaign_brief_form.field_name_label')}
          labelType="highlighted"
          helpNote={__t('campaign_brief_form.field_name_help_note')}
          direction="column"
          validators={[
            isRequired(briefDetails.name),
            maxLength(briefDetails.name, NAME_MAX_LENGTH)
          ]}
        >
          <Input
            name="title"
            width="responsive"
            value={briefDetails.name}
            placeholder={__t('campaign_brief_form.field_name_placeholder')}
            onChange={(event) => this.handleFieldChanged('name', event.target.value)}
          />
        </FormField>
      </div>
    );
  }

  renderMainImage() {
    const { campaignAssets } = this.props;

    return (
      <FormField
        fontSize="small"
        label={__t('campaign_brief_form.field_main_image_label')}
        helpNote={__t('campaign_brief_form.field_main_image_help_note')}
        labelType="highlighted"
        showValidationIcon={false}
        direction="column"
      >
        <div className="campaign-brief-form__field-main-image">
          <CampaignMainImageUpload
            mainImage={campaignAssets.mainImage}
          />
        </div>
      </FormField>
    );
  }

  renderCategory() {
    const { briefDetails } = this.state;
    const categories = CAMPAIGN_CATEGORIES.map(category => ({
      identifier: category,
      value: category,
      isDisabled: false,
      isFocused: false,
      label: __t(`campaign_brief_form.categories_labels.${category}`),
      isSelected: briefDetails.category === category
    }));
    return (
      <FormField
        showRequiredMark
        fontSize="small"
        label={__t('campaign_brief_form.field_category_label')}
        helpNote={__t('campaign_brief_form.field_category_help_note')}
        labelType="highlighted"
        showValidationIcon={false}
        direction="column"
        validators={[
          isRequired(briefDetails.category)
        ]}
      >
        <div className="campaign-brief-form__dropdown">
          <MixedSelectDropdown
            name="category"
            items={categories}
            withSelectedInList
            placeholder={__t('campaign_brief_form.field_category_placeholder')}
            onItemsToggle={(updatedItems, data, selectedItem) => {
              this.handleFieldChanged('category', selectedItem?.identifier || updatedItems[0].identifier);
            }}
          />
        </div>
      </FormField>
    );
  }

  renderBudget() {
    const { briefDetails } = this.state;

    return (
      <FormField
        fontSize="small"
        label={__t('campaign_brief_form.field_budget_label')}
        helpNote={__t('campaign_brief_form.field_budget_help_note')}
        labelType="highlighted"
        direction="column"
        showValidationIcon={false}
        validators={[
          isPositiveNumber(briefDetails.budget)
        ]}
      >
        <div className="campaign-brief-form__field-budget">
          <Input
            type="text"
            hint="€"
            hintAlign="right"
            name="budget"
            width="responsive"
            value={`${briefDetails.budget === null ? '' : String(briefDetails.budget)}`}
            onChange={(event) => {
              const digitFragments = event.target.value.match(/\d+/g);
              const parsedValue: number | undefined | null = digitFragments ? Number(digitFragments.join('')) : null;
              this.handleFieldChanged('budget', parsedValue);
            }}
          />
        </div>
      </FormField>
    );
  }

  renderTimeFrame() {
    const { briefDetails } = this.state;

    return (
      <FormField
        showValidationIcon={false}
        direction="column"
      >
        <div className="campaign-brief-form__columns">
          <div className="campaign-brief-form__column">
            <FormField
              showRequiredMark
              showValidationIcon={false}
              fontSize="small"
              label={__t('campaign_brief_form.field_timeframe_label')}
              labelType="highlighted"
              direction="column"
              validators={[
                isRequired(briefDetails.startDate),
                isRequired(briefDetails.endDate)
              ]}
            >
              <DatePickerMulti
                startDateName="startDate"
                startDate={briefDetails.startDate}
                endDateName="endDate"
                endDate={briefDetails.endDate}
                numMonths={1}
                showDateSelector
                numPastYearsInDateSelector={0}
                numFutureYearsInDateSelector={1}
                onChange={this.handleDateChange}
              />
            </FormField>
          </div>
        </div>
        <HelpNote
          message={__t('campaign_brief_form.field_dates_help_note')}
        />
      </FormField>
    );
  }

  renderDescription() {
    const { briefDetails } = this.state;
    const briefDetailsDescription = briefDetails.description;

    return (
      <FormField
        fontSize="small"
        label={__t('campaign_brief_form.field_description_label')}
        labelType="highlighted"
        helpNote={__t('campaign_brief_form.field_description_help_note')}
        showRequiredMark
        showValidationIcon={false}
        direction="column"
        validators={[
          isRequiredRichText(briefDetailsDescription),
          maxLength(briefDetailsDescription, LONG_STRING_MAX_LENGTH)
        ]}
      >
        <RichTextEditor
          key={briefDetailsDescription}
          canEdit
          value={briefDetailsDescription}
          onChange={(value) => this.handleFieldChanged('description', value)}
          placeholder={__t('campaign_brief_form.field_description_placeholder')}
        />
      </FormField>
    );
  }

  renderAdditionalRequirements() {
    const { briefDetails } = this.state;
    const briefDetailsAdditionalRequirementsTemplate = briefDetails.additionalRequirementsTemplate;

    return (
      <FormField
        fontSize="small"
        label={__t('campaign_brief_form.field_additional_requirements_label')}
        labelType="highlighted"
        helpNote={__t('campaign_brief_form.field_additional_requirements_help_note')}
        showValidationIcon={false}
        direction="column"
        validators={[
          maxLength(briefDetailsAdditionalRequirementsTemplate, LONG_STRING_MAX_LENGTH)
        ]}
      >
        <RichTextEditor
          key={briefDetailsAdditionalRequirementsTemplate}
          canEdit
          value={briefDetailsAdditionalRequirementsTemplate}
          onChange={(value) => this.handleFieldChanged('additionalRequirementsTemplate', value)}
          placeholder={__t('campaign_brief_form.field_additional_requirements_placeholder')}
        />
      </FormField>
    );
  }

  renderCampaignAssets() {
    const { campaignAssets } = this.props;

    return (
      <FormField
        fontSize="small"
        label={__t('campaign_brief_form.field_assets')}
        labelType="highlighted"
        helpNote={__t('campaign_brief_form.field_assets_help_note')}
        direction="column"
        showValidationIcon={false}
      >
        <CampaignAssets
          canEdit
          assets={campaignAssets.assets || []}
        />
      </FormField>
    );
  }

  renderManagers() {
    const { teamMembers } = this.props;
    const { briefDetails } = this.state;

    const items: Array<MixedSelectDropdownItem> = teamMembers
      .filter(member => !isNil(member.value))
      .map((member) => ({
        identifier: member.value ?? '',
        label: member.label,
        value: member.value ?? '',
        isDisabled: false,
        isFocused: false,
        isSelected: Boolean(
          briefDetails.managers.find((manager) => manager.value === member.value)
        )
      }));

    return (
      <FormField
        fontSize="small"
        label={__t('campaign_brief_form.field_managers')}
        labelType="highlighted"
        helpNote={__t('campaign_brief_form.field_managers_help_note')}
        direction="column"
        showValidationIcon={false}
      >
        <MixedSelectDropdown
          withMulti
          withSelectAll
          withRemovable
          withSearchable
          name="managers"
          placeholder={__t('campaign_brief_form.field_managers_placeholder')}
          items={items}
          onItemsToggle={this.handleManagersChange}
          onItemsToggleAll={this.handleManagersChange}
          onItemsRemove={this.handleManagersChange}
          onItemsRemoveAll={this.handleManagersChange}
        />
      </FormField>
    );
  }

  renderMetadataFields() {
    const { isZalandoMerchantsFieldHidden, organizationSettings } = this.props;
    const { briefDetails } = this.state;

    return (
      <div className="campaign-brief-form__meta-fields">
        <CampaignMetaFields
          brands={briefDetails.zalandoMeta.brands}
          merchants={briefDetails.zalandoMeta.merchants}
          onChangeMetaFields={this.handleMetaFieldChange}
          direction="row"
          onResetToDefault={this.handleResetToDefault}
          isBrandModified={!isEqual(briefDetails.zalandoMeta.brands, organizationSettings.zalandoMeta?.brands)}
          isMerchantModified={isZalandoMerchantsFieldHidden ? briefDetails.zalandoMeta.merchants.length > 0
            : !isEqual(briefDetails.zalandoMeta.merchants, organizationSettings.zalandoMeta?.merchants)}
        />
      </div>
    );
  }

  render() {
    const { onCancel, campaignAssets, campaign, title, inline, isAdmin } = this.props;
    const { isUpdated, isSubmitting } = this.state;

    return (
      <section className="campaign-brief-form">
        <Form
          onSubmit={this.handleSubmit}
        >
          {
            !isSubmitting && (isUpdated || campaignAssets.isUpdated) &&
              <PromptExit type="page" />
          }
          <PageSection
            margin="none"
            {...(inline ? { padding: 'none', style: 'none' } : null)}
            title={title}
            titleProps={{
              size: 'medium',
              color: 'light',
              margin: 'none',
              customClass: 'campaign-brief-form__heading'
            }}
          >
            <div className="campaign-brief-form__content">

              {this.renderName()}

              {this.renderMainImage()}

              {this.renderCategory()}

              {this.renderBudget()}

              {this.renderTimeFrame()}

              {this.renderDescription()}

              {this.renderAdditionalRequirements()}

              {this.renderCampaignAssets()}

              {this.renderManagers()}

              {
                isAdmin &&
                  this.renderMetadataFields()
              }

              <FormFooter
                showRequiredMarkExplanation
              />
            </div>
          </PageSection>
          <FormActions align="center">
            <ActionButton
              color="tertiary"
              onClick={onCancel}
            >
              {__t('campaign_brief_form.action_button_cancel')}
            </ActionButton>
            <div className="campaign-brief-form__button-save">
              <ActionButton
                color="primary"
                type="submit"
              >
                {__t(campaign
                  ? 'campaign_brief_form.action_button_save'
                  : 'campaign_brief_form.button_save')}
              </ActionButton>
              {
                this.doesOfferExist() && (
                  <div className="campaign-brief-form__button-help">
                    {__t('campaign_brief_form.button_save_help')}
                  </div>
                )
              }
            </div>
          </FormActions>
        </Form>
      </section>
    );
  }
}

const mapMemberToSelectItem = (member) => ({ value: String(member.id), label: getFullName(member) });

const mapStateToProps = ({ organizations, teamMembers, account, organizationSettings, runtimeConfig }): StateProps => ({
  currentOrganizationId: organizations.currentOrganizationId,
  teamMembers: (teamMembers.mapByOrganizationId[organizations.currentOrganizationId] || []).map(mapMemberToSelectItem),
  isManagedService: isManagedServiceSubscription(organizations.currentOrganization),
  isAdmin: isAdminAccount(account.currentAccount, account.roles),
  organizationSettings: organizationSettings.organization,
  isZalandoMerchantsFieldHidden: runtimeConfig.config?.isZalandoMerchantsFieldHidden
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchCloseNotification: () => dispatch(closeNotification()),
  dispatchFetchTeamMembers: (organizationId) => dispatch(fetchTeamMembers(organizationId))
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignBriefForm);
